import React, { useState, useEffect } from 'react';
import { 
    X, Download, Mail, Printer, RefreshCw, 
    CheckCircle, AlertCircle, Clock, CreditCard,
    Calendar, DollarSign, User, Home, FileText,
    Phone, Banknote, Smartphone, Receipt
} from 'lucide-react';
import api from '../services/api';
import Modal from './Common/Modal';

const PaymentDetailsModal = ({ isOpen, onClose, payment, onPaymentUpdate }) => {
    const [paymentDetails, setPaymentDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [showRefundModal, setShowRefundModal] = useState(false);
    const [refundReason, setRefundReason] = useState('');
    const [statusUpdate, setStatusUpdate] = useState('');

    useEffect(() => {
        if (isOpen && payment?.id) {
            fetchPaymentDetails();
        }
    }, [isOpen, payment]);

    const fetchPaymentDetails = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/payments/${payment.id}`);
            setPaymentDetails(response.data.data);
        } catch (error) {
            console.error('Error fetching payment details:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async () => {
        if (!statusUpdate) return;
        
        setUpdating(true);
        try {
            await api.put(`/payments/${payment.id}/status`, { status: statusUpdate });
            alert(`Payment marked as ${statusUpdate}`);
            fetchPaymentDetails();
            if (onPaymentUpdate) onPaymentUpdate();
        } catch (error) {
            console.error('Error updating payment status:', error);
            alert('Failed to update payment status');
        } finally {
            setUpdating(false);
            setStatusUpdate('');
        }
    };

    const handleRefund = async () => {
        if (!refundReason) {
            alert('Please provide a reason for refund');
            return;
        }
        
        setUpdating(true);
        try {
            await api.post(`/payments/${payment.id}/refund`, { reason: refundReason });
            alert('Payment refunded successfully');
            setShowRefundModal(false);
            setRefundReason('');
            fetchPaymentDetails();
            if (onPaymentUpdate) onPaymentUpdate();
        } catch (error) {
            console.error('Error processing refund:', error);
            alert('Failed to process refund');
        } finally {
            setUpdating(false);
        }
    };

    const downloadReceipt = async () => {
        try {
            const response = await api.get(`/payments/${payment.id}/receipt`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `receipt_${payment.id}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading receipt:', error);
            alert('Failed to download receipt');
        }
    };

    const emailReceipt = async () => {
        const email = prompt('Enter recipient email:', paymentDetails?.email);
        if (email) {
            setUpdating(true);
            try {
                await api.post(`/payments/${payment.id}/email-receipt`, { email });
                alert('Receipt sent successfully!');
            } catch (error) {
                console.error('Error sending email:', error);
                alert('Failed to send email');
            } finally {
                setUpdating(false);
            }
        }
    };

    const printReceipt = () => {
        window.open(`/api/payments/${payment.id}/receipt`, '_blank');
    };

    const getStatusBadge = (status) => {
        const badges = {
            completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Completed' },
            pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' },
            processing: { color: 'bg-blue-100 text-blue-800', icon: RefreshCw, label: 'Processing' },
            failed: { color: 'bg-red-100 text-red-800', icon: AlertCircle, label: 'Failed' },
            refunded: { color: 'bg-gray-100 text-gray-800', icon: RefreshCw, label: 'Refunded' }
        };
        const badge = badges[status] || badges.pending;
        const Icon = badge.icon;
        return (
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${badge.color}`}>
                <Icon className="h-4 w-4 mr-1" />
                {badge.label}
            </span>
        );
    };

    const getPaymentMethodIcon = (method) => {
        switch (method) {
            case 'cash': return <Banknote className="h-5 w-5 text-green-600" />;
            case 'mpesa_stk': return <Smartphone className="h-5 w-5 text-blue-600" />;
            case 'mpesa_paybill': return <Phone className="h-5 w-5 text-blue-600" />;
            case 'bank_transfer': return <CreditCard className="h-5 w-5 text-purple-600" />;
            default: return <CreditCard className="h-5 w-5 text-gray-600" />;
        }
    };

    const getAvailableStatuses = (currentStatus) => {
        const allStatuses = ['pending', 'processing', 'completed', 'failed', 'refunded'];
        return allStatuses.filter(s => s !== currentStatus);
    };

    if (loading) {
        return (
            <Modal isOpen={isOpen} onClose={onClose} title="Payment Details" size="lg">
                <div className="text-center py-8">Loading payment details...</div>
            </Modal>
        );
    }

    if (!paymentDetails) return null;

    const details = paymentDetails;

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} title="Payment Details" size="lg">
                <div className="space-y-6">
                    {/* Header with Receipt Number */}
                    <div className="flex justify-between items-start">
                        <div>
                            <div className="flex items-center space-x-2">
                                <Receipt className="h-6 w-6 text-indigo-600" />
                                <h2 className="text-xl font-bold text-gray-900">Payment Receipt</h2>
                            </div>
                            {details.receipt_number && (
                                <p className="text-sm text-gray-500 mt-1">Receipt No: {details.receipt_number}</p>
                            )}
                        </div>
                        <div className="flex space-x-2">
                            <button
                                onClick={downloadReceipt}
                                className="p-2 text-gray-600 hover:text-indigo-600"
                                title="Download Receipt"
                            >
                                <Download className="h-5 w-5" />
                            </button>
                            <button
                                onClick={emailReceipt}
                                className="p-2 text-gray-600 hover:text-blue-600"
                                title="Email Receipt"
                            >
                                <Mail className="h-5 w-5" />
                            </button>
                            <button
                                onClick={printReceipt}
                                className="p-2 text-gray-600 hover:text-gray-800"
                                title="Print Receipt"
                            >
                                <Printer className="h-5 w-5" />
                            </button>
                        </div>
                    </div>

                    {/* Status Section */}
                    <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex justify-between items-center">
                            <div>
                                <p className="text-sm text-gray-500">Payment Status</p>
                                {getStatusBadge(details.status)}
                            </div>
                            {details.status !== 'completed' && details.status !== 'refunded' && (
                                <select
                                    value={statusUpdate}
                                    onChange={(e) => setStatusUpdate(e.target.value)}
                                    className="text-sm border rounded-md px-2 py-1"
                                >
                                    <option value="">Update Status</option>
                                    {getAvailableStatuses(details.status).map(status => (
                                        <option key={status} value={status}>
                                            Mark as {status.charAt(0).toUpperCase() + status.slice(1)}
                                        </option>
                                    ))}
                                </select>
                            )}
                            {statusUpdate && (
                                <button
                                    onClick={handleStatusUpdate}
                                    disabled={updating}
                                    className="ml-2 px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700"
                                >
                                    Apply
                                </button>
                            )}
                            {details.status === 'pending' && (
                                <button
                                    onClick={() => setShowRefundModal(true)}
                                    className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                                >
                                    Refund
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Payment Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-white border rounded-lg p-4">
                            <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                                <DollarSign className="h-5 w-5 mr-1 text-indigo-600" />
                                Payment Information
                            </h3>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-500">Amount:</span>
                                    <span className="text-lg font-bold text-indigo-600">
                                        KES {parseFloat(details.amount).toLocaleString()}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-500">Payment Type:</span>
                                    <span className="text-sm font-medium capitalize">{details.payment_type}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-500">Payment Method:</span>
                                    <div className="flex items-center space-x-1">
                                        {getPaymentMethodIcon(details.payment_method)}
                                        <span className="text-sm capitalize">{details.payment_method?.replace('_', ' ')}</span>
                                    </div>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-500">Payment Date:</span>
                                    <span className="text-sm">{new Date(details.created_at).toLocaleString()}</span>
                                </div>
                                {details.transaction_date && (
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-500">Transaction Date:</span>
                                        <span className="text-sm">{new Date(details.transaction_date).toLocaleString()}</span>
                                    </div>
                                )}
                                {details.mpesa_receipt_number && (
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-500">M-Pesa Receipt:</span>
                                        <span className="text-sm font-mono">{details.mpesa_receipt_number}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="bg-white border rounded-lg p-4">
                            <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                                <User className="h-5 w-5 mr-1 text-indigo-600" />
                                Tenant Information
                            </h3>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-500">Name:</span>
                                    <span className="text-sm font-medium">{details.first_name} {details.last_name}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-500">Email:</span>
                                    <span className="text-sm">{details.email}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-500">Phone:</span>
                                    <span className="text-sm">{details.phone || 'N/A'}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Lease Information */}
                    <div className="bg-white border rounded-lg p-4">
                        <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                            <FileText className="h-5 w-5 mr-1 text-indigo-600" />
                            Lease Information
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-sm text-gray-500">Lease Number</p>
                                <p className="font-medium">{details.lease_number}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Property</p>
                                <p className="font-medium">{details.asset_name}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Unit</p>
                                <p className="font-medium">{details.unit_number || 'Main'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Payment Period</p>
                                <p className="font-medium">
                                    {details.payment_period_start && details.payment_period_end ? (
                                        `${new Date(details.payment_period_start).toLocaleDateString()} - ${new Date(details.payment_period_end).toLocaleDateString()}`
                                    ) : 'N/A'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Notes Section */}
                    {details.metadata?.notes && (
                        <div className="bg-gray-50 rounded-lg p-4">
                            <h3 className="font-semibold text-gray-900 mb-2">Notes</h3>
                            <p className="text-sm text-gray-600">{details.metadata.notes}</p>
                        </div>
                    )}

                    {/* Refund Information */}
                    {details.status === 'refunded' && details.metadata?.refund_reason && (
                        <div className="bg-red-50 rounded-lg p-4">
                            <h3 className="font-semibold text-red-800 mb-2 flex items-center">
                                <AlertCircle className="h-4 w-4 mr-1" />
                                Refund Information
                            </h3>
                            <p className="text-sm text-red-700">Reason: {details.metadata.refund_reason}</p>
                            <p className="text-sm text-red-700">Refunded At: {new Date(details.metadata.refunded_at).toLocaleString()}</p>
                        </div>
                    )}
                </div>
            </Modal>

            {/* Refund Modal */}
            <Modal isOpen={showRefundModal} onClose={() => setShowRefundModal(false)} title="Refund Payment" size="md">
                <div className="space-y-4">
                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <p className="text-sm text-yellow-800">
                            You are about to refund payment of <strong>KES {parseFloat(details.amount).toLocaleString()}</strong>
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Reason for Refund
                        </label>
                        <textarea
                            value={refundReason}
                            onChange={(e) => setRefundReason(e.target.value)}
                            rows={3}
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            placeholder="Please provide a reason for this refund..."
                            required
                        />
                    </div>
                    <div className="flex justify-end space-x-3">
                        <button
                            onClick={() => setShowRefundModal(false)}
                            className="px-4 py-2 border rounded-lg"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleRefund}
                            disabled={updating}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                        >
                            {updating ? 'Processing...' : 'Confirm Refund'}
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default PaymentDetailsModal;